import { useTranslation, Trans } from 'react-i18next';
import {
    Stack,
    Paper,
    Button,
    Typography,
    Dialog,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { StringRotation } from '../StringRotation';
import { Input } from './Input';
import { AssistantMessage } from './Messages';
import { StepInfo } from './StepInfo';
import {
    StepInfoPlan,
    StepInfoForecast,
    BriefingPreviewProps,
    ShowBriefings,
    ResumeForecast,
    ConfirmFinalPlan,
} from './ContentPlan';
import { Branding, BrandingProps } from './Branding';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { CompetitorPreviewProps, CompetitorsPreview } from './CompetitorsPreview';
import { ProfilePreview, ProfilePreviewProps } from './ProfilePreview';
import { GetHandle } from './GetHandle';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { useAndieChat } from '@/providers/andie-chat-provider';
import { memo, useEffect, useRef } from 'react';
import type { UIMessage } from 'ai';
import Markdown from 'react-markdown';
import { motion } from 'framer-motion';
import { COOKIES } from '@/constants';
import Cookies from 'js-cookie';

type ToolPart = {
    type: string;
    toolName?: string;
    toolCallId: string;
    state: string;
    input?: unknown;
    output?: unknown;
};

function getToolName(part: ToolPart): string {
    if (part.toolName) return part.toolName;
    if (part.type.startsWith('tool-')) return part.type.slice(5);
    return part.type;
}

function mapStatus(state: string): `${ActionResponseStatusAi}` {
    if (state === 'output-available' || state === 'output-error' || state === 'output-denied') {
        return ActionResponseStatusAi.COMPLETE;
    }
    return ActionResponseStatusAi.EXECUTING;
}

const ErrorModal = ({ message, reset }: { message: string; reset: () => void }) => {
    const { t } = useTranslation();

    return (
        <Dialog open>
            <DialogContent>
                <Typography>{message}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={reset}>{t('onboarding.chat.yes-start-over')}</Button>
            </DialogActions>
        </Dialog>
    );
};

const OnboardingToolRender = ({
    part,
    isFirstReset,
    hasBriefingResult,
    onEnd,
    reset,
}: {
    part: ToolPart;
    isFirstReset: boolean;
    hasBriefingResult: boolean;
    defaultHandle: string;
    onEnd: (option: string) => void;
    reset: () => void;
}) => {
    const { t } = useTranslation();
    const toolName = getToolName(part);
    const status = mapStatus(part.state);
    const result = part.output as ActionWorkflowAi | undefined;

    switch (toolName) {
        case 'resetExperience': {
            if (!isFirstReset) return null;
            const reason = (part.input as Record<string, unknown>)?.reason;
            return (
                <ErrorModal
                    message={
                        reason === 'no-profile-found'
                            ? t('onboarding.chat.no-profile-found')
                            : t('onboarding.chat.reset-experience')
                    }
                    reset={reset}
                />
            );
        }

        case 'getInstagramHandle':
            return <GetHandle />;

        case 'publicProfileWorkflow':
            return status !== ActionResponseStatusAi.COMPLETE ||
                (status === ActionResponseStatusAi.COMPLETE && result?.result) ? (
                <Stack direction="column" gap={1} alignItems="left">
                    <StepInfo
                        status={status}
                        currentStep={1}
                        totalSteps={5}
                        titleComp={t('onboarding.chat.profile-details')}
                        processingComp={
                            <StringRotation
                                messages={[
                                    t('onboarding.chat.finding-profile'),
                                    t('onboarding.chat.getting-profile-info'),
                                    t('onboarding.chat.sorting-top-posts'),
                                    t('onboarding.chat.analyzing-engagement'),
                                ]}
                                intervalMs={3000}
                            />
                        }
                        finishedComp={<>{t('onboarding.chat.profile-info-title')}</>}
                    />
                    {status === ActionResponseStatusAi.COMPLETE && !!result?.result && (
                        <ProfilePreview {...(result.result as ProfilePreviewProps)} />
                    )}
                </Stack>
            ) : null;

        case 'profileClassifyWorkflow': {
            const classifyResult = result?.result as
                | { categoryName: string; justification: string }
                | undefined;
            return status !== ActionResponseStatusAi.COMPLETE ||
                (status === ActionResponseStatusAi.COMPLETE && classifyResult) ? (
                <Stack direction="column" gap={1} alignItems="left">
                    <StepInfo
                        status={status}
                        currentStep={2}
                        totalSteps={5}
                        titleComp={t('onboarding.chat.classify-profile')}
                        processingComp={
                            <StringRotation
                                messages={[
                                    t('onboarding.chat.categorizing-profile'),
                                    t('onboarding.chat.reading-profiles'),
                                    t('onboarding.chat.justifying-categorization'),
                                ]}
                                intervalMs={3000}
                            />
                        }
                        finishedComp={
                            <Trans
                                i18nKey="onboarding.chat.profile-category-title"
                                values={{ category: classifyResult?.categoryName }}
                            />
                        }
                    />
                    {status === ActionResponseStatusAi.COMPLETE && classifyResult?.justification && (
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Typography variant="caption">{classifyResult.justification}</Typography>
                        </Paper>
                    )}
                </Stack>
            ) : null;
        }

        case 'profileBrandingWorkflow':
            return status !== ActionResponseStatusAi.COMPLETE ||
                (status === ActionResponseStatusAi.COMPLETE && result?.result) ? (
                <Stack gap={1} direction="column" alignItems="left">
                    <StepInfo
                        status={status}
                        currentStep={3}
                        totalSteps={5}
                        titleComp={t('onboarding.chat.define-profile-branding')}
                        processingComp={
                            <StringRotation
                                messages={[
                                    t('onboarding.chat.branding-analyzing'),
                                    t('onboarding.chat.finding-branding-elements'),
                                    t('onboarding.chat.finding-visual-style'),
                                    t('onboarding.chat.analyzing-content-tone'),
                                    t('onboarding.chat.identifying-key-messaging'),
                                ]}
                                intervalMs={3000}
                            />
                        }
                        finishedComp={
                            <Trans
                                i18nKey="onboarding.chat.branding-analysis-title"
                                values={{ name: (result?.result as BrandingProps)?.name }}
                            />
                        }
                    />
                    {status === ActionResponseStatusAi.COMPLETE && !!result?.result && (
                        <Branding {...(result.result as BrandingProps)} />
                    )}
                </Stack>
            ) : null;

        case 'profileCompetitorsWorkflow':
            return status !== ActionResponseStatusAi.COMPLETE ||
                (status === ActionResponseStatusAi.COMPLETE && result?.result) ? (
                <Stack direction="column" gap={1} alignItems="left">
                    <StepInfo
                        status={status}
                        currentStep={3}
                        totalSteps={5}
                        titleComp={t('onboarding.chat.find-best-competitors')}
                        processingComp={
                            <StringRotation
                                messages={[
                                    t('onboarding.chat.competitors-analyzing'),
                                    t('onboarding.chat.finding-competitors'),
                                    t('onboarding.chat.analyzing-competitor-content'),
                                    t('onboarding.chat.identifying-competitor-strategies'),
                                    t('onboarding.chat.finding-market-gaps'),
                                    t('onboarding.chat.finding-differentiation'),
                                    t('onboarding.chat.analyzing-engagement-strategies'),
                                    t('onboarding.chat.identifying-engagement-tactics'),
                                    t('onboarding.chat.analyzing-audience-engagement'),
                                    t('onboarding.chat.analyzing-marketing-strategies'),
                                ]}
                                intervalMs={5000}
                            />
                        }
                        finishedComp={
                            <Trans
                                i18nKey="onboarding.chat.competitors-title"
                                values={{
                                    count: Array.isArray(result?.result)
                                        ? (result.result as CompetitorPreviewProps).length
                                        : 0,
                                }}
                            />
                        }
                    />
                    {status === ActionResponseStatusAi.COMPLETE && !!result?.result && (
                        <CompetitorsPreview data={result.result as CompetitorPreviewProps} />
                    )}
                </Stack>
            ) : null;

        case 'generatePlanningWorkflow': {
            if (hasBriefingResult) return null;
            const planResult = result?.result as { dates: BriefingPreviewProps[] } | undefined;
            return status !== ActionResponseStatusAi.COMPLETE ||
                (status === ActionResponseStatusAi.COMPLETE && planResult) ? (
                <Stack direction="column" gap={1} alignItems="left">
                    <StepInfoPlan status={status} />
                    {planResult && <ShowBriefings data={planResult.dates} />}
                </Stack>
            ) : null;
        }

        case 'generatePostBriefingWorkflow': {
            const briefingResult = result?.result as
                | { onboardingId: string; briefings: BriefingPreviewProps[] }
                | undefined;
            return status === ActionResponseStatusAi.COMPLETE && briefingResult ? (
                <BriefingStep briefingResult={briefingResult} status={status} />
            ) : null;
        }

        case 'generatePlanningResumeForecastWorkflow':
            return (
                <Stack direction="column" gap={1} alignItems="left">
                    <StepInfoForecast status={status} />
                    <ResumeForecast finalData={result?.result ?? null} />
                    {!!result?.result && <ConfirmFinalPlan onEnd={onEnd} />}
                </Stack>
            );

        default:
            return null;
    }
};

const BriefingStep = ({
    briefingResult,
    status,
}: {
    briefingResult: { onboardingId: string; briefings: BriefingPreviewProps[] };
    status: `${ActionResponseStatusAi}`;
}) => {
    useEffect(() => {
        if (briefingResult?.onboardingId) {
            Cookies.set(COOKIES.ONBOARDING_ID, String(briefingResult.onboardingId), { expires: 7 });
        }
    }, [briefingResult?.onboardingId]);

    return (
        <Stack direction="column" gap={1} alignItems="left">
            <StepInfoPlan status={status} />
            <ShowBriefings data={briefingResult.briefings} />
        </Stack>
    );
};

const OnboardingUserMessage = memo(({ message }: { message: UIMessage }) => {
    const textContent = message.parts
        .filter((p) => p.type === 'text')
        .map((p) => p.text)
        .join('');

    if (!textContent) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{ display: 'inline-block' }}
        >
            <Stack pb={3}>
                <Paper
                    sx={{
                        px: 2,
                        maxWidth: '80%',
                        alignSelf: 'flex-end',
                        backgroundColor: 'andie.main',
                        color: 'primary.contrastText',
                        borderRadius: 4,
                        wordBreak: 'break-word',
                    }}
                >
                    <Typography component="span">
                        <Markdown
                            components={{
                                a: ({ children, href, ...props }) => (
                                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {textContent}
                        </Markdown>
                    </Typography>
                </Paper>
            </Stack>
        </motion.div>
    );
});

const OnboardingAssistantMessage = memo(
    ({
        message,
        defaultHandle,
        onEnd,
        reset,
    }: {
        message: UIMessage;
        defaultHandle: string;
        onEnd: (option: string) => void;
        reset: () => void;
    }) => {
        const textContent = message.parts
            .filter((p) => p.type === 'text')
            .map((p) => p.text)
            .join('');

        const isToolPart = (p: { type: string }) =>
            p.type === 'dynamic-tool' || (p.type.startsWith('tool-') && p.type !== 'text');
        const toolParts = message.parts.filter(isToolPart) as unknown as ToolPart[];

        let resetSeen = false;
        const hasSuccessfulWorkflow = toolParts.some(
            (p) => getToolName(p) !== 'resetExperience' && p.state === 'output-available',
        );
        const hasBriefingResult = toolParts.some(
            (p) =>
                getToolName(p) === 'generatePostBriefingWorkflow' &&
                p.state === 'output-available' &&
                !!(p.output as ActionWorkflowAi | undefined)?.result,
        );

        return (
            <Stack py={1} gap={1}>
                {textContent && <AssistantMessage message={{ content: textContent }} isLoading={false} />}
                {toolParts.map((part) => {
                    const isReset = getToolName(part) === 'resetExperience';
                    const isFirstReset = isReset && !resetSeen && !hasSuccessfulWorkflow;
                    if (isReset) resetSeen = true;

                    return (
                        <OnboardingToolRender
                            key={part.toolCallId}
                            part={part}
                            isFirstReset={isFirstReset}
                            hasBriefingResult={hasBriefingResult}
                            defaultHandle={defaultHandle}
                            onEnd={onEnd}
                            reset={reset}
                        />
                    );
                })}
            </Stack>
        );
    },
);

export const Chat = ({
    onStart,
    onEnd,
    defaultHandle = '',
    restartOnScratch = false,
    onRestart,
}: {
    onStart: () => void;
    onEnd: (option: string) => void;
    defaultHandle?: string;
    restartOnScratch?: boolean;
    onRestart?: () => void;
}) => {
    const { t } = useTranslation();
    const { messages, sendMessage, setMessages, setThreadId, isLoading } = useAndieChat();
    const bottomRef = useRef<HTMLDivElement>(null);
    const hasStarted = useRef(false);

    const reset = () => {
        setMessages([]);
        setThreadId(crypto.randomUUID());
    };

    const handleEnd = (option: string) => {
        if (option === 'scratch' && restartOnScratch) {
            // Restart Andie in place: clear stale briefings and conversation,
            // keep the user on the same step instead of advancing.
            Cookies.remove(COOKIES.ONBOARDING_ID);
            reset();
            onRestart?.();
            return;
        }
        onEnd(option);
    };

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [messages]);

    const handleSend = (text: string) => {
        if (!hasStarted.current) {
            hasStarted.current = true;
            onStart();
        }
        sendMessage(text);
    };

    return (
        <Stack maxWidth={1000} width="100%" mx="auto" py={2}>
            <Stack
                sx={{
                    flex: 1,
                    minHeight: 0,
                    px: 2,
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                <AssistantMessage
                    message={{ content: t('onboarding.chat.initial-message') }}
                    isLoading={false}
                />
                {messages.map((message) =>
                    message.role === 'assistant' ? (
                        <OnboardingAssistantMessage
                            key={message.id}
                            message={message}
                            defaultHandle={defaultHandle}
                            onEnd={handleEnd}
                            reset={reset}
                        />
                    ) : message.role === 'user' ? (
                        <OnboardingUserMessage key={message.id} message={message} />
                    ) : null,
                )}
                {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                    <Stack py={2}>
                        <CircularProgress size={24} />
                    </Stack>
                )}
                <div ref={bottomRef} />
            </Stack>
            <Stack sx={{ position: 'relative', zIndex: 2 }}>
                <Input onSend={handleSend} inProgress={isLoading} countMessages={messages.length} />
            </Stack>
        </Stack>
    );
};
